export type MetricFormat = 'count' | 'percent' | 'decimal' | 'currency';
export interface Metric { value: number; target: number; available?: boolean }
export interface TimelinePoint { date: string; values: Record<string, number | null> }
export interface KpiData {
  range: { from: string; to: string; timezone: string };
  generated_at: string;
  definitions_version: string;
  metrics: Record<string, Metric>;
  timeline?: { interval: string; points: TimelinePoint[] };
}
export const definitions = [
  { key: 'verified_creators', label: 'Verified Creators', format: 'count', direction: 'up', strict: false, note: 'Creator ที่ยืนยันแล้วทั้งหมด ณ วันนั้น' },
  { key: 'creator_first_review_rate', label: 'Creator → First Review', format: 'percent', direction: 'up', strict: true, note: 'Creator ที่ยืนยันในช่วงนี้และมีรีวิวแรกภายใน 7 วัน ÷ Creator ที่ยืนยันในช่วงนี้ ณ วันนั้น' },
  { key: 'reviews_per_active_creator', label: 'Reviews / Active Creator', format: 'decimal', direction: 'up', strict: true, note: 'รีวิวสะสมตั้งแต่วันเริ่มช่วง ÷ Creator ที่มีรีวิวในช่วงถึงวันนั้น' },
  { key: 'qualified_reviews', label: 'Qualified Reviews', format: 'count', direction: 'up', strict: false, note: 'รีวิวที่ผ่านเกณฑ์สะสมตั้งแต่วันเริ่มช่วง' },
  { key: 'active_merchants', label: 'Active Merchants', format: 'count', direction: 'up', strict: false, note: 'ร้านที่ยืนยันแล้วและมีความเคลื่อนไหวใน 30 วันย้อนหลัง ณ วันนั้น' },
  { key: 'cost_per_activated_creator_thb', label: 'Cost / Activated Creator', format: 'currency', direction: 'down', strict: true, note: 'ค่าใช้จ่ายที่บันทึกสะสมในช่วง ÷ Creator ที่มีรีวิวในช่วงถึงวันนั้น · ยิ่งต่ำยิ่งดี · หากยังไม่มีรายการค่าใช้จ่ายจะแสดงว่ายังไม่มีข้อมูล' },
] as const;
export type Definition = typeof definitions[number];

export function formatValue(value: number | null, format: MetricFormat, compact = false) {
  if (value === null || !Number.isFinite(value)) return 'ยังไม่มีข้อมูล';
  const number = (format === 'percent' ? value * 100 : value).toLocaleString('th-TH', {
    maximumFractionDigits: format === 'count' ? 0 : format === 'currency' ? 2 : 1,
    notation: compact && format === 'count' ? 'compact' : 'standard',
  });
  return `${number}${format === 'percent' ? '%' : format === 'currency' ? ' บาท' : ''}`;
}
export function targetLabel(metric: Metric, definition: Definition) {
  const operator = definition.direction === 'down' ? '<' : definition.strict ? '>' : '≥';
  return `${operator} ${formatValue(metric.target, definition.format)}`;
}
export function metricStatus(metric: Metric, definition: Definition) {
  if (metric.available === false) return 'no-data';
  const achieved = definition.direction === 'down' ? metric.value < metric.target
    : definition.strict ? metric.value > metric.target : metric.value >= metric.target;
  if (achieved) return 'on-track';
  const attainment = definition.direction === 'down' ? metric.target / Math.max(metric.value, .0001)
    : metric.value / Math.max(metric.target, .0001);
  return attainment >= .7 ? 'watch' : 'off-track';
}
export function timelineChange(points: TimelinePoint[], key: string) {
  const first = points[0]?.values[key];
  const last = points.at(-1)?.values[key];
  return typeof first === 'number' && Number.isFinite(first) && typeof last === 'number' && Number.isFinite(last) ? last - first : null;
}
export function lineSegments(values: (number | null)[]) {
  const segments: { index: number; value: number }[][] = [];
  let segment: { index: number; value: number }[] = [];
  for (let index = 0; index < values.length; index++) {
    const value = values[index];
    if (value === null || !Number.isFinite(value)) {
      if (segment.length) segments.push(segment);
      segment = [];
    } else segment.push({ index, value });
  }
  if (segment.length) segments.push(segment);
  return segments;
}
export function chartCeiling(values: (number | null)[], benchmark: number, format: MetricFormat) {
  const valid = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (format === 'percent') return Math.max(1, benchmark, ...valid);
  const maximum = Math.max(benchmark, 1, ...valid);
  const raw = maximum * 1.15;
  const scale = 10 ** Math.floor(Math.log10(raw));
  return Math.ceil(raw / scale * 2) / 2 * scale;
}
export function bangkokDate(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}
export function shiftDate(date: string, days: number) {
  return new Date(new Date(`${date}T00:00:00Z`).getTime() + days * 86400000).toISOString().slice(0, 10);
}
export function dateLabel(date: string, full = false) {
  return new Date(`${date}T12:00:00+07:00`).toLocaleDateString('th-TH', {
    timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', ...(full ? { year: '2-digit' } : {}),
  });
}
